// Purveyor slice P4 (docs/2026-09-27-purveyor-design.md): purveyor accounts
// and box authoring. A purveyor login is parallel to a household account,
// not part of it, so this page needs no householdId -- it loads a signed-in
// user's OWN purveyor memberships/invites, plus the shared directory (already
// proven correct in select-all-ordering.test.mjs) to get at that purveyor's
// own catalog and boxes. Always in the nav, like every other page -- most
// visitors will simply see the "you don't manage any purveyors" explainer,
// the same way Inventory doesn't hide itself from a household with nothing
// in it yet.
//
// Split into subtabs (feedback #57, #58, using SubtabLayout): Boxes (P4),
// Catalog (products) and Profile (details, pickup locations, hours). This
// component loads the shared data and handles invites; the subtabs are the
// Portal*Tab components below, routed under /purveyor-portal in App.jsx.
import { useEffect, useState } from "react";
import {
  loadMyPurveyorMemberships,
  loadMyPendingPurveyorInvites,
  acceptPurveyorInvite,
  declinePurveyorInvite,
  loadPurveyorDirectory,
  createBox,
  updateBox,
  setBoxStatus,
  deleteBox,
  addBoxItem,
  updateBoxItem,
  deleteBoxItem,
  cloneBox,
  markBoxDelivered,
  updatePurveyor,
} from "../lib/db";
import { useOutletContext } from "react-router-dom";
import { SubtabLayout } from "../components/SubtabLayout";
import { PurveyorProfileForm, PurveyorLocationsEditor, PurveyorHoursEditor } from "../components/PurveyorProfileEditor";
import { PurveyorCatalogEditor } from "../components/PurveyorCatalogEditor";
import { PORTAL_SUBTABS } from "../lib/navigation";
import { useMealPlanner } from "../hooks/MealPlannerContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { COLORS, FONT_SIZE, SPACE, RADIUS } from "../lib/constants";
import { capitalizeFirst, formatQuantity } from "../lib/utils";

const emptyItemDraft = () => ({ mode: "catalog", productId: "", oneOffName: "", quantity: "", unit: "" });

export function PurveyorPortalPage() {
  const { colors } = useMealPlanner();
  const { card, ink, soft, accent, danger, line } = colors;

  const [memberships, setMemberships] = useState(null); // null = still loading
  const [pendingInvites, setPendingInvites] = useState([]);
  const [purveyorData, setPurveyorData] = useState({}); // { [purveyorId]: the directory entry, as loadPurveyorDirectory returns it }
  const [selectedId, setSelectedId] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [inviteBusyId, setInviteBusyId] = useState(null);
  const [inviteError, setInviteError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([loadMyPurveyorMemberships(), loadMyPendingPurveyorInvites()])
      .then(async ([mems, invites]) => {
        const data = await loadPurveyorEntries(mems.map((m) => m.purveyorId));
        if (cancelled) return;
        setMemberships(mems);
        setPendingInvites(invites);
        setPurveyorData(data);
        setSelectedId(mems[0]?.purveyorId || null);
      })
      .catch((e) => {
        console.error("[PurveyorPortalPage] load failed:", e);
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleAccept(invite) {
    setInviteBusyId(invite.id);
    setInviteError(null);
    try {
      await acceptPurveyorInvite(invite.id);
      const data = await loadPurveyorEntries([invite.purveyorId]);
      setPurveyorData((prev) => ({ ...prev, ...data }));
      setMemberships((prev) => [...(prev || []), { purveyorId: invite.purveyorId, purveyorName: invite.purveyorName }]);
      setPendingInvites((prev) => prev.filter((i) => i.id !== invite.id));
      setSelectedId((prev) => prev || invite.purveyorId);
    } catch (e) {
      setInviteError(e.message || "Couldn't accept that invite — try again.");
    } finally {
      setInviteBusyId(null);
    }
  }

  async function handleDecline(invite) {
    setInviteBusyId(invite.id);
    setInviteError(null);
    try {
      await declinePurveyorInvite(invite.id);
      setPendingInvites((prev) => prev.filter((i) => i.id !== invite.id));
    } catch (e) {
      setInviteError(e.message || "Couldn't decline that invite — try again.");
    } finally {
      setInviteBusyId(null);
    }
  }

  // Applies `updater` to the selected purveyor's entry.
  function updateSelected(updater) {
    setPurveyorData((prev) => ({ ...prev, [selectedId]: updater(prev[selectedId]) }));
  }

  async function reloadSelected() {
    const data = await loadPurveyorEntries([selectedId]);
    setPurveyorData((prev) => ({ ...prev, ...data }));
    setMemberships((prev) => prev.map((m) => (m.purveyorId === selectedId && data[selectedId] ? { ...m, purveyorName: data[selectedId].name } : m)));
  }

  if (loadError) {
    return <p className="mp-sans" style={{ color: danger }}>Couldn't load the purveyor portal — try reloading the page.</p>;
  }
  if (memberships === null) {
    return <p className="mp-sans" style={{ color: soft }}>Loading…</p>;
  }

  const selected = selectedId ? purveyorData[selectedId] : null;

  return (
    <div>
      <p className="mp-sans" style={{ fontSize: FONT_SIZE.sm, color: soft, marginBottom: SPACE.xl2, maxWidth: 640 }}>
        Manage the purveyors you're a member of: build boxes and mark them Delivered, keep your catalog of products current, and edit how you appear in the directory.
      </p>

      {pendingInvites.length > 0 && (
        <div style={{ marginBottom: SPACE.xl2, display: "flex", flexDirection: "column", gap: SPACE.md }}>
          {pendingInvites.map((invite) => (
            <div key={invite.id} className="mp-sans" style={{ background: card, border: `1px solid ${line}`, borderRadius: RADIUS.xl, padding: "var(--space-xl2) var(--space-xxl)" }}>
              <p style={{ fontSize: FONT_SIZE.md, margin: "0 0 var(--space-lg)", color: ink }}>
                You've been invited to manage <strong>{invite.purveyorName}</strong> on The Weekly Kitchen.
              </p>
              <div style={{ display: "flex", gap: SPACE.lg, flexWrap: "wrap" }}>
                <button className="mp-btn" disabled={inviteBusyId === invite.id} onClick={() => handleAccept(invite)} style={{ background: accent, color: COLORS.onAccent, padding: "var(--space-sm) var(--space-xl)", borderRadius: RADIUS.lg, fontSize: FONT_SIZE.base, fontWeight: 600 }}>
                  Accept
                </button>
                <button className="mp-btn" disabled={inviteBusyId === invite.id} onClick={() => handleDecline(invite)} style={{ background: "transparent", border: `1px solid ${line}`, color: ink, padding: "var(--space-sm) var(--space-xl)", borderRadius: RADIUS.lg, fontSize: FONT_SIZE.base }}>
                  Decline
                </button>
              </div>
            </div>
          ))}
          {inviteError && <p className="mp-sans" style={{ color: danger, fontSize: FONT_SIZE.sm }}>{inviteError}</p>}
        </div>
      )}

      {memberships.length === 0 ? (
        pendingInvites.length === 0 && (
          <p className="mp-sans" style={{ color: soft }}>
            You don't manage any purveyors yet. If you run a CSA, farmers market, farm stand, country store or REKO circle and want to list it here, get in touch and we'll set you up.
          </p>
        )
      ) : (
        <>
          {memberships.length > 1 && (
            <div style={{ display: "flex", gap: SPACE.sm, flexWrap: "wrap", marginBottom: SPACE.xl2 }}>
              {memberships.map((m) => (
                <button
                  key={m.purveyorId}
                  className="mp-btn mp-sans"
                  onClick={() => setSelectedId(m.purveyorId)}
                  style={{ background: selectedId === m.purveyorId ? accent : "transparent", color: selectedId === m.purveyorId ? COLORS.onAccent : soft, border: `1px solid ${line}`, padding: "var(--space-sm) var(--space-lg)", borderRadius: RADIUS.pill, fontSize: FONT_SIZE.sm }}
                >
                  {m.purveyorName}
                </button>
              ))}
            </div>
          )}

          {selected && (
            <SubtabLayout
              tabs={PORTAL_SUBTABS}
              label="Purveyor portal sections"
              context={{ purveyorId: selectedId, purveyor: selected, updateSelected, reloadSelected }}
            />
          )}
        </>
      )}
    </div>
  );
}

// Re-reads the shared directory and keeps the entries for these purveyors.
async function loadPurveyorEntries(purveyorIds) {
  const directory = await loadPurveyorDirectory();
  const byId = new Map(directory.map((p) => [p.id, p]));
  const data = {};
  purveyorIds.forEach((id) => {
    if (byId.has(id)) data[id] = byId.get(id);
  });
  return data;
}

// The three subtabs (routes under /purveyor-portal in App.jsx). Each reads
// the selected purveyor from PurveyorPortalPage through the outlet context,
// and is keyed by purveyor id so switching purveyors drops any open edit.

export function PortalBoxesTab() {
  const { purveyorId, purveyor, updateSelected } = useOutletContext();
  const { colors } = useMealPlanner();
  const isMobile = useIsMobile();
  const inputStyle = { boxSizing: "border-box", padding: "var(--space-sm) var(--space-md)", borderRadius: RADIUS.md, border: `1px solid ${colors.line}`, background: COLORS.card, color: colors.ink, fontSize: FONT_SIZE.base, fontFamily: "var(--font-sans)" };
  return (
    <PurveyorBoxes
      key={purveyorId}
      purveyorId={purveyorId}
      purveyor={purveyor}
      colors={colors}
      isMobile={isMobile}
      inputStyle={inputStyle}
      onBoxesChange={(updater) => updateSelected((p) => ({ ...p, boxes: updater(p.boxes) }))}
    />
  );
}

export function PortalCatalogTab() {
  const { purveyorId, purveyor, updateSelected } = useOutletContext();
  const { colors } = useMealPlanner();
  const isMobile = useIsMobile();
  return (
    <div>
      <h2 style={{ fontSize: FONT_SIZE.h3, margin: "0 0 var(--space-xl2)" }}>{purveyor.name}</h2>
      <PurveyorCatalogEditor key={purveyorId} purveyorId={purveyorId} purveyor={purveyor} onChange={updateSelected} colors={colors} isMobile={isMobile} />
    </div>
  );
}

export function PortalProfileTab() {
  const { purveyorId } = useOutletContext();
  return <PortalProfile key={purveyorId} />;
}

function PortalProfile() {
  const { purveyorId, purveyor, updateSelected, reloadSelected } = useOutletContext();
  const { colors } = useMealPlanner();
  const isMobile = useIsMobile();
  const [saved, setSaved] = useState(false);
  const panel = { background: colors.card, border: `1px solid ${colors.line}`, borderRadius: RADIUS.xl, padding: "var(--space-xl2) var(--space-xxl)" };
  const heading = (text) => <h3 style={{ fontSize: FONT_SIZE.lg, margin: "0 0 var(--space-lg)" }}>{text}</h3>;

  return (
    <div className="mp-sans" style={{ display: "flex", flexDirection: "column", gap: SPACE.xl }}>
      <p style={{ fontSize: FONT_SIZE.sm, color: colors.soft, margin: 0, maxWidth: 640 }}>
        This is how {purveyor.name} appears in the Purveyors directory. {purveyor.isVerified ? "It's marked Verified." : "It isn't marked Verified yet."} Only the app admin can change that, or add or remove a purveyor.
      </p>
      <div style={panel}>
        {heading("Details")}
        <PurveyorProfileForm
          initial={purveyor}
          colors={colors}
          isMobile={isMobile}
          onSubmit={async (profile) => {
            setSaved(false);
            await updatePurveyor(purveyorId, profile, purveyor.purveyorTypes);
            await reloadSelected();
            setSaved(true);
          }}
        />
        {saved && <p style={{ fontSize: FONT_SIZE.sm, color: colors.accent, margin: "var(--space-md) 0 0" }}>Saved.</p>}
      </div>
      <div style={panel}>
        {heading("Pickup locations")}
        <PurveyorLocationsEditor
          purveyorId={purveyorId}
          locations={purveyor.pickupLocations}
          onChange={(updater) => updateSelected((p) => ({ ...p, pickupLocations: updater(p.pickupLocations) }))}
          colors={colors}
          isMobile={isMobile}
        />
      </div>
      <div style={panel}>
        {heading("Hours")}
        <PurveyorHoursEditor
          purveyorId={purveyorId}
          hours={purveyor.hours}
          locations={purveyor.pickupLocations}
          onChange={(updater) => updateSelected((p) => ({ ...p, hours: updater(p.hours) }))}
          colors={colors}
          isMobile={isMobile}
        />
      </div>
    </div>
  );
}

function PurveyorBoxes({ purveyorId, purveyor, colors, isMobile, inputStyle, onBoxesChange }) {
  const { soft, accent, danger } = colors;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function handleNewBox() {
    setBusy(true);
    setError("");
    try {
      const box = await createBox(purveyorId);
      onBoxesChange((boxes) => [{ ...box, items: [] }, ...boxes]);
    } catch {
      setError("Couldn't create a new box — try again.");
    } finally {
      setBusy(false);
    }
  }

  const sorted = [...purveyor.boxes].sort((a, b) => (b.deliveryDate || "").localeCompare(a.deliveryDate || ""));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: SPACE.xl2 }}>
        <h2 style={{ fontSize: FONT_SIZE.h3, margin: 0 }}>{purveyor.name}</h2>
        <button className="mp-btn mp-sans" disabled={busy} onClick={handleNewBox} style={{ background: accent, color: COLORS.onAccent, padding: "var(--space-sm) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm, fontWeight: 600 }}>
          + New box
        </button>
      </div>
      {error && <p className="mp-sans" style={{ color: danger, fontSize: FONT_SIZE.sm, marginBottom: SPACE.lg }}>{error}</p>}

      {sorted.length === 0 ? (
        <p className="mp-sans" style={{ color: soft }}>No boxes yet — create one above.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: SPACE.lg }}>
          {sorted.map((box) => (
            <BoxCard key={box.id} box={box} products={purveyor.products} colors={colors} isMobile={isMobile} inputStyle={inputStyle} purveyorId={purveyorId} onBoxesChange={onBoxesChange} />
          ))}
        </div>
      )}
    </div>
  );
}

function BoxCard({ box, products, colors, isMobile, inputStyle, purveyorId, onBoxesChange }) {
  const { card, ink, soft, accent, danger, line } = colors;
  const isLocked = box.status === "Delivered";

  const [editingMeta, setEditingMeta] = useState(false);
  const [metaDraft, setMetaDraft] = useState({ name: box.name, deliveryDate: box.deliveryDate || "" });
  const [addingItem, setAddingItem] = useState(false);
  const [itemDraft, setItemDraft] = useState(emptyItemDraft());
  const [editingItemId, setEditingItemId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [confirmDeliver, setConfirmDeliver] = useState(false);
  const [confirmDeleteBox, setConfirmDeleteBox] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function replaceBox(patch) {
    onBoxesChange((boxes) => boxes.map((b) => (b.id === box.id ? { ...b, ...patch } : b)));
  }
  function replaceItems(updater) {
    onBoxesChange((boxes) => boxes.map((b) => (b.id === box.id ? { ...b, items: updater(b.items) } : b)));
  }

  async function saveMeta() {
    setBusy(true);
    setError("");
    try {
      await updateBox(box.id, metaDraft);
      replaceBox({ name: metaDraft.name.trim(), deliveryDate: metaDraft.deliveryDate || "" });
      setEditingMeta(false);
    } catch {
      setError("Couldn't save — try again.");
    } finally {
      setBusy(false);
    }
  }

  async function toggleConfirmedStatus() {
    const next = box.status === "Planned" ? "Confirmed" : "Planned";
    setBusy(true);
    setError("");
    try {
      await setBoxStatus(box.id, next);
      replaceBox({ status: next });
    } catch {
      setError("Couldn't change status — try again.");
    } finally {
      setBusy(false);
    }
  }

  async function doMarkDelivered() {
    setBusy(true);
    setError("");
    try {
      await markBoxDelivered(box.id);
      replaceBox({
        status: "Delivered",
        items: box.items.map((it) => ({ ...it, snapshotName: it.snapshotName || (it.productId ? products.find((p) => p.id === it.productId)?.name : it.oneOffName) || "(item)" })),
      });
      setConfirmDeliver(false);
    } catch (e) {
      setError(e.message || "Couldn't mark this box Delivered — try again.");
    } finally {
      setBusy(false);
    }
  }

  async function doDeleteBox() {
    setBusy(true);
    setError("");
    try {
      await deleteBox(box.id);
      onBoxesChange((boxes) => boxes.filter((b) => b.id !== box.id));
    } catch {
      setError("Couldn't delete this box — try again.");
      setBusy(false);
    }
  }

  async function doClone() {
    setBusy(true);
    setError("");
    try {
      const clone = await cloneBox(purveyorId, box);
      const items = box.items.map((it, i) => ({ id: `${clone.id}-tmp-${i}`, productId: it.productId, oneOffName: it.oneOffName, quantity: it.quantity, unit: it.unit, snapshotName: "" }));
      onBoxesChange((boxes) => [{ ...clone, items }, ...boxes]);
    } catch {
      setError("Couldn't clone this box — try again.");
    } finally {
      setBusy(false);
    }
  }

  async function submitNewItem() {
    const payload = itemDraft.mode === "catalog" ? { productId: itemDraft.productId, quantity: itemDraft.quantity, unit: itemDraft.unit } : { oneOffName: itemDraft.oneOffName, quantity: itemDraft.quantity, unit: itemDraft.unit };
    if (itemDraft.mode === "catalog" && !itemDraft.productId) return;
    if (itemDraft.mode === "one-off" && !itemDraft.oneOffName.trim()) return;
    setBusy(true);
    setError("");
    try {
      const created = await addBoxItem(box.id, payload);
      replaceItems((items) => [...items, created]);
      setItemDraft(emptyItemDraft());
      setAddingItem(false);
    } catch {
      setError("Couldn't add that item — try again.");
    } finally {
      setBusy(false);
    }
  }

  function startEditItem(it) {
    setEditingItemId(it.id);
    setEditDraft({ mode: it.productId ? "catalog" : "one-off", productId: it.productId || "", oneOffName: it.oneOffName || "", quantity: String(it.quantity || ""), unit: it.unit || "" });
  }

  async function saveEditItem() {
    const payload = editDraft.mode === "catalog" ? { productId: editDraft.productId, quantity: editDraft.quantity, unit: editDraft.unit } : { oneOffName: editDraft.oneOffName, quantity: editDraft.quantity, unit: editDraft.unit };
    setBusy(true);
    setError("");
    try {
      await updateBoxItem(editingItemId, payload);
      replaceItems((items) =>
        items.map((it) =>
          it.id === editingItemId
            ? { ...it, productId: payload.productId || null, oneOffName: payload.oneOffName || "", quantity: Number(payload.quantity) || 0, unit: payload.unit || "" }
            : it
        )
      );
      setEditingItemId(null);
      setEditDraft(null);
    } catch {
      setError("Couldn't save that item — try again.");
    } finally {
      setBusy(false);
    }
  }

  async function doDeleteItem(itemId) {
    setBusy(true);
    setError("");
    try {
      await deleteBoxItem(itemId);
      replaceItems((items) => items.filter((it) => it.id !== itemId));
    } catch {
      setError("Couldn't remove that item — try again.");
    } finally {
      setBusy(false);
    }
  }

  const statusColors = { Planned: [COLORS.stoneTint, ink], Confirmed: [COLORS.skyTint, COLORS.skyInk], Delivered: [COLORS.accentTint, accent] };
  const [statusBg, statusFg] = statusColors[box.status] || statusColors.Planned;

  return (
    <div style={{ background: card, border: `1px solid ${line}`, borderRadius: RADIUS.lg, padding: isMobile ? "var(--space-lg)" : "var(--space-lg) var(--space-xxl)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: SPACE.md, flexWrap: "wrap", marginBottom: SPACE.sm }}>
        {editingMeta ? (
          <div style={{ display: "flex", gap: SPACE.sm, flexWrap: "wrap", flex: 1 }}>
            <input style={{ ...inputStyle, flex: 1, minWidth: 140 }} placeholder="Box name" value={metaDraft.name} onChange={(e) => setMetaDraft((d) => ({ ...d, name: e.target.value }))} />
            <input style={inputStyle} type="date" value={metaDraft.deliveryDate} onChange={(e) => setMetaDraft((d) => ({ ...d, deliveryDate: e.target.value }))} />
            <button className="mp-btn mp-sans" disabled={busy} onClick={saveMeta} style={{ background: accent, color: COLORS.onAccent, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
              Save
            </button>
            <button className="mp-btn mp-sans" onClick={() => setEditingMeta(false)} style={{ background: "transparent", border: `1px solid ${line}`, color: ink, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
              Cancel
            </button>
          </div>
        ) : (
          <button
            className="mp-sans"
            onClick={() => !isLocked && setEditingMeta(true)}
            disabled={isLocked}
            style={{ background: "none", border: "none", padding: 0, textAlign: "left", cursor: isLocked ? "default" : "pointer", color: ink, fontWeight: 700, fontSize: FONT_SIZE.md }}
          >
            {box.name || "(untitled box)"}
            {box.deliveryDate ? ` — ${box.deliveryDate}` : ""}
          </button>
        )}
        <span className="mp-sans" style={{ background: statusBg, color: statusFg, padding: "var(--space-hair) var(--space-md)", borderRadius: RADIUS.pill, fontSize: FONT_SIZE.xs, fontWeight: 600 }}>
          {box.status}
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: SPACE.xs, marginBottom: SPACE.md }}>
        {box.items.map((it) => {
          const product = it.productId ? products.find((p) => p.id === it.productId) : null;
          const label = it.snapshotName || product?.name || it.oneOffName || "(item)";
          const amount = it.quantity > 0 ? `${formatQuantity(it.quantity)}${it.unit ? ` ${it.unit}` : ""}` : "";
          if (isLocked || editingItemId !== it.id) {
            return (
              <div key={it.id} className="mp-sans" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: SPACE.md, fontSize: FONT_SIZE.sm }}>
                <button
                  onClick={() => !isLocked && startEditItem(it)}
                  disabled={isLocked}
                  style={{ background: "none", border: "none", padding: 0, textAlign: "left", cursor: isLocked ? "default" : "pointer", color: ink, flex: 1 }}
                >
                  {capitalizeFirst(label)}
                </button>
                <span style={{ color: soft }}>{amount}</span>
                {!isLocked && (
                  <button className="mp-btn" onClick={() => doDeleteItem(it.id)} disabled={busy} title="Remove" aria-label={`Remove ${label}`} style={{ background: "transparent", color: danger, fontSize: FONT_SIZE.lg, padding: "0 var(--space-xs)" }}>
                    ×
                  </button>
                )}
              </div>
            );
          }
          return (
            <ItemForm key={it.id} draft={editDraft} setDraft={setEditDraft} products={products} inputStyle={inputStyle} colors={colors} onSubmit={saveEditItem} onCancel={() => { setEditingItemId(null); setEditDraft(null); }} busy={busy} submitLabel="Save" />
          );
        })}
      </div>

      {!isLocked && (
        addingItem ? (
          <ItemForm draft={itemDraft} setDraft={setItemDraft} products={products} inputStyle={inputStyle} colors={colors} onSubmit={submitNewItem} onCancel={() => { setAddingItem(false); setItemDraft(emptyItemDraft()); }} busy={busy} submitLabel="Add" />
        ) : (
          <button className="mp-btn mp-sans" onClick={() => setAddingItem(true)} style={{ background: "transparent", color: accent, padding: "var(--space-xs) 0", fontSize: FONT_SIZE.sm, fontWeight: 600 }}>
            + Add item
          </button>
        )
      )}

      {error && <p className="mp-sans" style={{ color: danger, fontSize: FONT_SIZE.sm, marginTop: SPACE.sm }}>{error}</p>}

      <div style={{ display: "flex", gap: SPACE.sm, flexWrap: "wrap", marginTop: SPACE.md, borderTop: `1px dashed ${line}`, paddingTop: SPACE.md }}>
        {!isLocked && (
          <button className="mp-btn mp-sans" disabled={busy} onClick={toggleConfirmedStatus} style={{ background: "transparent", border: `1px solid ${line}`, color: ink, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
            {box.status === "Planned" ? "Mark Confirmed" : "Back to Planned"}
          </button>
        )}
        {box.status === "Confirmed" &&
          (confirmDeliver ? (
            <span style={{ display: "flex", alignItems: "center", gap: SPACE.sm }}>
              <span className="mp-sans" style={{ fontSize: FONT_SIZE.sm, color: soft }}>Mark Delivered? This locks the box for good.</span>
              <button className="mp-btn mp-sans" disabled={busy} onClick={doMarkDelivered} style={{ background: accent, color: COLORS.onAccent, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
                Mark Delivered
              </button>
              <button className="mp-btn mp-sans" onClick={() => setConfirmDeliver(false)} style={{ background: "transparent", border: `1px solid ${line}`, color: ink, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
                Cancel
              </button>
            </span>
          ) : (
            <button className="mp-btn mp-sans" onClick={() => setConfirmDeliver(true)} style={{ background: "transparent", color: accent, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm, fontWeight: 600 }}>
              Mark Delivered
            </button>
          ))}
        <button className="mp-btn mp-sans" disabled={busy} onClick={doClone} style={{ background: "transparent", border: `1px solid ${line}`, color: ink, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
          Clone
        </button>
        {!isLocked &&
          (confirmDeleteBox ? (
            <span style={{ display: "flex", alignItems: "center", gap: SPACE.sm }}>
              <button className="mp-btn mp-sans" disabled={busy} onClick={doDeleteBox} style={{ background: danger, color: COLORS.onAccent, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
                Delete box
              </button>
              <button className="mp-btn mp-sans" onClick={() => setConfirmDeleteBox(false)} style={{ background: "transparent", border: `1px solid ${line}`, color: ink, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
                Cancel
              </button>
            </span>
          ) : (
            <button className="mp-btn mp-sans" onClick={() => setConfirmDeleteBox(true)} style={{ background: "transparent", color: danger, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
              Delete box
            </button>
          ))}
      </div>
    </div>
  );
}

function ItemForm({ draft, setDraft, products, inputStyle, colors, onSubmit, onCancel, busy, submitLabel }) {
  const { ink, soft, accent, line } = colors;
  return (
    <div style={{ display: "flex", gap: SPACE.sm, flexWrap: "wrap", alignItems: "center", padding: "var(--space-xs) 0" }}>
      <select
        style={{ ...inputStyle, minWidth: 90 }}
        value={draft.mode}
        onChange={(e) => setDraft((d) => ({ ...d, mode: e.target.value }))}
      >
        <option value="catalog">From catalog</option>
        <option value="one-off">One-off</option>
      </select>
      {draft.mode === "catalog" ? (
        <select style={{ ...inputStyle, flex: 1, minWidth: 140 }} value={draft.productId} onChange={(e) => setDraft((d) => ({ ...d, productId: e.target.value }))}>
          <option value="">Choose a product…</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      ) : (
        <input style={{ ...inputStyle, flex: 1, minWidth: 140 }} placeholder="One-time item name" value={draft.oneOffName} onChange={(e) => setDraft((d) => ({ ...d, oneOffName: e.target.value }))} />
      )}
      <input style={{ ...inputStyle, width: 80 }} placeholder="Amount" value={draft.quantity} onChange={(e) => setDraft((d) => ({ ...d, quantity: e.target.value }))} />
      <input style={{ ...inputStyle, width: 80 }} placeholder="Unit" value={draft.unit} onChange={(e) => setDraft((d) => ({ ...d, unit: e.target.value }))} />
      <button className="mp-btn mp-sans" disabled={busy} onClick={onSubmit} style={{ background: accent, color: COLORS.onAccent, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
        {submitLabel}
      </button>
      <button className="mp-btn mp-sans" onClick={onCancel} style={{ background: "transparent", border: `1px solid ${line}`, color: ink, padding: "var(--space-xs) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
        Cancel
      </button>
    </div>
  );
}
